import { notifyBugsnagFromErrorBoundary } from '@salescore/client-base'
import { type ReactElement, useCallback, useReducer, useState } from 'react'
import { ErrorBoundary } from 'react-error-boundary'

import type { OpenView } from '../../domain/chatTarget'
import { hasPromptText } from '../../domain/promptText'
import { nextWidgetMode } from '../../domain/widgetMode'
import { useAgentChat } from '../../hooks/useAgentChat'
import { useAgentStatus } from '../../hooks/useAgentStatus'
import {
  AgentCanSendContext,
  AgentChatActionsContext,
  AgentChatContext,
  AgentRunningTurnContext,
  AgentTurnStoppingContext,
} from '../../state/agentWidgetContext'
import {
  selectCanSendToOpenChat,
  selectCanStartNewChat,
  selectIsOpenTurnStopping,
  selectOpenedChatCount,
  selectOpenRunningTurnId,
  selectOpenSessionChat,
} from '../../state/agentWidgetState'
import { type AgentApprovalPolicy, loadApprovalPolicy, saveApprovalPolicy } from '../prompt/approvalMode'
import { AgentFrame } from './AgentFrame'
import { AgentLauncherButton } from './AgentLauncherButton'
import { loadPanelSize } from './panelSize'

interface AgentWidgetProperties {
  organizationId: string
  // 今開いている view。新しいチャットの最初の指示を送るときに、そのチャットの会話の対象にする（domain/chatTarget.ts）
  openView: OpenView | null
}

// 表示のモード、利用者が変えたパネルの大きさ、書いている指示、会話（useAgentChat）を持ち、入口か枠を出す。
// ウィジェットはページの差し替え（MemberLayoutBase の SwitchTransition）の外に描かれ、ページを移っても作り直されないので、
// ここで持てば、ページをまたいで残る。閉じて入口に戻しても、実行中のターンの読み取りは続く
const AgentWidgetBody = ({ organizationId, openView }: AgentWidgetProperties): ReactElement => {
  const [mode, dispatch] = useReducer(nextWidgetMode, 'collapsed')
  // 再読み込みしても、利用者が変えたパネルの大きさで出す（ブラウザごとの設定）。読むのは最初に描くときだけ
  const [panelSize, setPanelSize] = useState(loadPanelSize)
  // 書いている指示。モードを変えても、閉じても消さない（仕様の「書きかけの指示」）
  const [prompt, setPrompt] = useState('')
  // API が受け付けなかった指示を入力欄に戻す。送った後に書き始めた文は上書きしない（空白だけなら戻す）
  const restoreRejectedPrompt = useCallback((rejected: string): void => {
    setPrompt((current) => (hasPromptText(current) ? current : rejected))
  }, [])
  // 承認モード。再読み込みしても選んだものが残るよう、ブラウザに覚える（approvalMode.ts）。読むのは最初に描くときだけ
  const [approvalPolicy, setApprovalPolicy] = useState(() => loadApprovalPolicy(organizationId))
  const changeApprovalPolicy = useCallback(
    (selected: AgentApprovalPolicy): void => {
      saveApprovalPolicy(organizationId, selected)
      setApprovalPolicy(selected)
    },
    [organizationId],
  )
  const { state, actions } = useAgentChat(organizationId, restoreRejectedPrompt, approvalPolicy, openView)
  const session = selectOpenSessionChat(state)
  // 枠に渡す処理は、Esc と 30 秒の数え直しのリスナーを描くたびに付け直さないよう、同じ関数を使い回す
  const expand = useCallback((): void => {
    dispatch('expand')
  }, [])
  const close = useCallback((): void => {
    dispatch('close')
  }, [])
  const timeOutIdleCompact = useCallback((): void => {
    dispatch('idleTimedOut')
  }, [])

  return (
    <AgentChatActionsContext.Provider value={actions}>
      <AgentChatContext.Provider value={session}>
        <AgentRunningTurnContext.Provider value={selectOpenRunningTurnId(state)}>
          <AgentTurnStoppingContext.Provider value={selectIsOpenTurnStopping(state)}>
            <AgentCanSendContext.Provider value={selectCanSendToOpenChat(state)}>
              {mode === 'collapsed' ? (
                <AgentLauncherButton
                  onOpen={() => {
                    dispatch('open')
                  }}
                />
              ) : (
                <AgentFrame
                  mode={mode}
                  panelSize={panelSize}
                  prompt={prompt}
                  canStartNewChat={selectCanStartNewChat(state)}
                  openedChatCount={selectOpenedChatCount(state)}
                  approvalPolicy={approvalPolicy}
                  onPromptChange={setPrompt}
                  onApprovalPolicyChange={changeApprovalPolicy}
                  onExpand={expand}
                  onClose={close}
                  onIdleTimeout={timeOutIdleCompact}
                  onPanelResize={setPanelSize}
                />
              )}
            </AgentCanSendContext.Provider>
          </AgentTurnStoppingContext.Provider>
        </AgentRunningTurnContext.Provider>
      </AgentChatContext.Provider>
    </AgentChatActionsContext.Provider>
  )
}

// 組織と利用者でエージェント機能を使える（status が enabled）ときだけ、入口か枠を出す（PdM の要件 P-3）。
// 読み終えるまでと、読めなかったときは出さない
const AgentWidgetGate = ({ organizationId, openView }: AgentWidgetProperties): ReactElement | null =>
  useAgentStatus(organizationId) === 'enabled' ? (
    <AgentWidgetBody organizationId={organizationId} openView={openView} />
  ) : null

// 画面右下に常駐するエージェントのウィジェット。描く側（MemberLayoutBase）が組織の機能フラグで描くかを決め、ここで status を読んで出すかを決める。
// 中の例外が既存の画面のエラー境界（ErrorProvider）まで届くと画面全体がエラーの表示になるので、ここで止めてウィジェットだけを消す
// status の判定とウィジェットの状態は組織のものなので、組織が変わったら key で作り直す。画面の組織の切り替え（signInAs）は今はページを読み直すが、
// それに頼ると、読み直さずに組織が変わったときに、新しい組織の status を読み終えるまで前の組織の判定で入口が出る
export const AgentWidget = ({ organizationId, openView }: AgentWidgetProperties): ReactElement => (
  <ErrorBoundary
    key={organizationId}
    fallback={null}
    onError={(error) => {
      notifyBugsnagFromErrorBoundary({ error: error instanceof Error ? error : new Error('unknown error') })
    }}
  >
    <AgentWidgetGate organizationId={organizationId} openView={openView} />
  </ErrorBoundary>
)
